/**
 * What a reasoning model thought, above its answer.
 *
 * While it thinks: the sparkle, "Thinking" (for how long, given `since`),
 * and the newest finished sentence of the chain on one faint line, paced by
 * `pace` so each stays up long enough to read and cross-faded into the next.
 * Once the answer starts, and on a stored reply: "Thought for 7s". Either
 * opens the whole chain below,
 * drawn like the prototype's steps list; while the chain is still growing the
 * panel follows its end unless the reader has scrolled up. A reply that
 * showed no reasoning shows nothing once its answer starts.
 */

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { useStickToBottom } from '../hooks/useStickToBottom';
import { elapsed, pace, reasoningHead, reasoningTail, tailBudget, thoughtFor, type Pace } from './reasoningText';
import { Ph } from './ui';

const row = { display: 'flex', alignItems: 'center', gap: 8, height: 28, minWidth: 0 } as const;

/** `.fade-in`'s length, which the outgoing line fades out over. */
const FADE_MS = 180;

interface Shown {
  id: string;
  text: string;
  /** The start of an unfinished sentence, which loses its end when it does
   *  not fit; a finished one loses its start. */
  head: boolean;
}

// Right-to-left for a finished sentence, so one that still does not fit
// loses its start rather than its end; the text inside stays left-to-right.
const lineStyle = (head: boolean): CSSProperties => ({
  display: 'block',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  direction: head ? 'ltr' : 'rtl',
  textAlign: 'left'
});

function fadeOut(el: HTMLElement | null) {
  el?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: FADE_MS, fill: 'forwards' });
}

/** The thinking line's words: the new line fades in as the old one fades out
 *  on top of it, and simply replaces it under reduced motion. */
function ChainLine({ id, text, head }: Shown) {
  const [leaving, setLeaving] = useState<Shown | null>(null);
  const last = useRef<Shown>({ id, text, head });

  useEffect(() => {
    const was = last.current;
    if (was.id === id || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    setLeaving(was);
    const timer = setTimeout(() => setLeaving(null), FADE_MS);
    return () => clearTimeout(timer);
  }, [id]);
  // After the effect above, so it still sees the line being replaced.
  useEffect(() => {
    last.current = { id, text, head };
  });

  return (
    <span
      aria-hidden="true"
      style={{ position: 'relative', flex: '0 1 auto', minWidth: 0, fontSize: 13, color: 'var(--text-faint)' }}
    >
      <span key={id} className="fade-in" style={lineStyle(head)}>
        <span dir="ltr">{text}</span>
      </span>
      {leaving ? (
        <span key={leaving.id} ref={fadeOut} style={{ ...lineStyle(leaving.head), position: 'absolute', inset: 0 }}>
          <span dir="ltr">{leaving.text}</span>
        </span>
      ) : null}
    </span>
  );
}

export function Reasoning({
  text,
  seconds,
  thinking,
  since = null
}: {
  /** The chain so far, or all of it. */
  text: string;
  /** How long it thought, once the answer has started. */
  seconds: number | null;
  /** Still thinking: nothing of the answer has arrived. */
  thinking: boolean;
  /** While thinking: since when (ms), for how long it has been. */
  since?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const { scrollRef, contentRef, stick } = useStickToBottom();
  // The thinking line's width, so the tail is cut to fit it.
  const [line, setLine] = useState<HTMLElement | null>(null);
  const [budget, setBudget] = useState(120);
  // What the line shows, and a tick for when that is due to change with no
  // new reasoning to prompt it.
  const [paced, setPaced] = useState<Pace | null>(null);
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!thinking || since === null) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [thinking, since]);
  // The time is left out of what a screen reader hears: it changes every second.
  const took = thinking && since !== null ? elapsed((now - since) / 1000) : '';
  const label = thinking ? (
    <>
      Thinking
      {took ? <span aria-hidden="true"> for {took}</span> : null}
    </>
  ) : (
    thoughtFor(seconds)
  );

  useEffect(() => {
    if (!line) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setBudget(tailBudget(entry.contentRect.width));
    });
    observer.observe(line);
    return () => observer.disconnect();
  }, [line]);

  useEffect(() => {
    if (thinking && text) setPaced((p) => pace(p, text, Date.now()));
  }, [thinking, text, tick]);

  useEffect(() => {
    const wake = thinking ? paced?.wake : null;
    if (wake == null) return;
    const timer = setTimeout(() => setTick((n) => n + 1), Math.max(0, wake - Date.now()));
    return () => clearTimeout(timer);
  }, [thinking, paced]);

  if (!thinking && !text) return null;

  const sparkle = (
    <Ph
      name="sparkle"
      size={15}
      color={thinking ? 'var(--primary)' : 'var(--text-faint)'}
      className={thinking ? 'pulse' : undefined}
      style={{ flex: 'none' }}
    />
  );

  // Nothing to open yet.
  if (!text) {
    return (
      <div role="status" style={{ ...row, fontSize: 13.5, color: 'var(--text-muted)' }}>
        {sparkle}
        {label}
      </div>
    );
  }

  const shown: Shown | null =
    thinking && !open && paced?.line
      ? {
          id: paced.id,
          text: paced.open ? reasoningHead(paced.line, budget) : reasoningTail(paced.line, budget),
          head: paced.open
        }
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div
        ref={thinking ? setLine : undefined}
        role={thinking ? 'status' : undefined}
        style={{ display: 'flex', minWidth: 0 }}
      >
        <button
          type="button"
          className="bare hov-soft"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => {
            // Opening mid-thought starts at the newest line.
            if (!open && thinking) stick();
            setOpen(!open);
          }}
          style={{
            ...row,
            flex: '0 1 auto',
            maxWidth: 'calc(100% + 8px)',
            padding: '0 8px',
            marginLeft: -8,
            borderRadius: 'var(--radius-sm)',
            fontSize: thinking ? 13.5 : 13,
            color: thinking ? 'var(--text-muted)' : 'var(--text-faint)'
          }}
        >
          {sparkle}
          <span style={{ flex: 'none' }}>{label}</span>
          {shown ? <ChainLine {...shown} /> : null}
          <Ph name={open ? 'caret-up' : 'caret-down'} size={12} style={{ flex: 'none' }} />
        </button>
      </div>

      {open ? (
        <div
          id={panelId}
          ref={thinking ? scrollRef : undefined}
          className="scroll"
          style={{ maxHeight: 320, overflowY: 'auto', paddingLeft: 10, borderLeft: '2px solid var(--border-subtle)' }}
        >
          <div
            ref={thinking ? contentRef : undefined}
            className="selectable"
            style={{
              padding: '5px 10px',
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--text-muted)',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word'
            }}
          >
            {text}
          </div>
        </div>
      ) : null}
    </div>
  );
}
